import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet } from "react-router-dom";
import logoUrl from "@/assets/codemonk-logo-long-black.svg";
import { cn } from "@/lib/utils";

const navClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-full px-4 py-2 font-heading text-base font-medium no-underline transition-colors",
    isActive ? "bg-secondary-subtle text-heading" : "text-fg-muted hover:bg-surface-muted hover:text-heading",
  );

export function AppShell({ children, orgSwitcher, extraNav }: { children?: ReactNode; orgSwitcher?: ReactNode; extraNav?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-toast focus:rounded-full focus:bg-surface focus:px-4 focus:py-2"
      >
        {t("common.skipToContent")}
      </a>
      <header className="sticky top-0 z-header border-b border-border bg-surface backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center" aria-label={t("common.appName")}>
            <img src={logoUrl} alt={t("common.logoAlt")} width={144} height={20} className="h-5 w-auto" />
          </Link>
          {orgSwitcher}
          <nav aria-label={t("common.nav.main")} className="ml-auto flex items-center gap-1">
            <NavLink to="/" end className={navClass}>
              {t("common.nav.home")}
            </NavLink>
            {extraNav}
            <NavLink to="/account" className={navClass}>
              {t("common.nav.account")}
            </NavLink>
          </nav>
        </div>
      </header>
      <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        {children ?? <Outlet />}
      </main>
      <footer className="border-t border-border bg-surface">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-6 text-sm text-fg-muted sm:px-6">
          <span>{t("common.footer.copyright")}</span>
          <nav aria-label={t("common.footer.legal")} className="ml-auto flex gap-4">
            <Link to="/privacy" className="text-fg-muted underline">
              {t("common.footer.privacy")}
            </Link>
            <Link to="/terms" className="text-fg-muted underline">
              {t("common.footer.terms")}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
